import React, { useState } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Select,
  DatePicker,
  Button,
  Statistic,
  Space,
  Typography,
  theme,
} from "antd";
import {
  DownloadOutlined,
  ThunderboltOutlined,
  DollarOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";
import dayjs from "dayjs";

const { Title, Text } = Typography;

export default function Statement() {
  const { token } = theme.useToken();
  const [pageSize, setPageSize] = useState(10);
  const [selectedGroups, setSelectedGroups] = useState(["all"]);
  const [selectedMonth, setSelectedMonth] = useState(dayjs("2026-01"));

  // TODO: Fetch daily stats from backend when API is ready
  const dailyStats = [];

  const totals = dailyStats.reduce(
    (acc, day) => ({
      dispatchCount: acc.dispatchCount + day.dispatchCount,
      dischargeKwh: acc.dischargeKwh + day.dischargeKwh,
      revenue: acc.revenue + day.revenue,
    }),
    { dispatchCount: 0, dischargeKwh: 0, revenue: 0 }
  );

  // TODO: Fetch group options from backend when API is ready
  const groupOptions = [
    { value: "all", label: "All Groups" },
  ];

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Dispatch Count",
      dataIndex: "dispatchCount",
      key: "dispatchCount",
      align: "right",
    },
    {
      title: "Discharge (kWh)",
      dataIndex: "dischargeKwh",
      key: "dischargeKwh",
      align: "right",
      render: (val) => val.toFixed(1),
    },
    {
      title: "Revenue",
      dataIndex: "revenue",
      key: "revenue",
      align: "right",
      render: (val) => (
        <Text style={{ color: token.colorSuccess, fontWeight: 500 }}>
          ${val.toFixed(2)}
        </Text>
      ),
    },
  ];

  const handleExport = () => {
    // In real implementation, call API to export CSV
    console.log("Exporting CSV for:", selectedGroups, selectedMonth.format("YYYY-MM"));
  };

  return (
    <PageLayout active="statement">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 4 }}>
          Statement
        </Title>
        <Text type="secondary" style={{ fontSize: 16 }}>
          View detailed dispatch statistics and revenue
        </Text>
      </div>

      {/* Filters */}
      <Card bordered={false} style={{ marginBottom: 24, borderRadius: 12 }}>
        <Space wrap size="middle">
          <div>
            <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
              Groups
            </Text>
            <Select
              value={selectedGroups[0]}
              onChange={(value) => setSelectedGroups([value])}
              options={groupOptions}
              style={{ width: 200 }}
              size="large"
            />
          </div>
          <div>
            <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
              Month
            </Text>
            <DatePicker
              picker="month"
              value={selectedMonth}
              onChange={(date) => setSelectedMonth(date)}
              size="large"
              style={{ width: 200 }}
            />
          </div>
          <div style={{ paddingTop: 28 }}>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              size="large"
              onClick={handleExport}
            >
              Export CSV
            </Button>
          </div>
        </Space>
      </Card>

      {/* Summary Cards */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Total Dispatches"
              value={totals.dispatchCount}
              prefix={<BarChartOutlined />}
              valueStyle={{ color: token.colorPrimary }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Total Discharge"
              value={totals.dischargeKwh}
              precision={1}
              suffix="kWh"
              prefix={<ThunderboltOutlined />}
              valueStyle={{ color: token.colorWarning }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Total Revenue"
              value={totals.revenue}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ color: token.colorSuccess }}
              formatter={(value) => `$${Number(value).toLocaleString("en-AU", { minimumFractionDigits: 2 })}`}
            />
          </Card>
        </Col>
      </Row>

      {/* Data Table */}
      <Card
        title="Daily Statistics"
        bordered={false}
        style={{ borderRadius: 12 }}
        styles={{
          header: { borderBottom: `1px solid ${token.colorBorderSecondary}` },
        }}
      >
        <Table
          columns={columns}
          dataSource={dailyStats}
          pagination={makePagination(pageSize, setPageSize)}
        />
      </Card>
    </PageLayout>
  );
}
